import type { LightSourceParams, LightingParams } from '@/types';
import { findPreset } from './lightingPresets';
import type { LightingStyle } from './lightingPresets';

export interface LightingRequestParams {
  lighting_style: LightingStyle;
}

/**
 * 生成参数映射（方案 §7）：结构化灯光参数 → fal `image-apps-v2/relighting` 请求参数。
 * 打灯不使用提示词：模型无 prompt 入参，主体一致性由专用 relighting 模型自身保证。
 *
 * 映射以主灯（isMain === true，即首盏）为准；补灯仅参与 3D 预览与参数记录
 * （模型单次仅接受一个灯光风格）。
 */
export function buildLightingParams(params: LightingParams): LightingRequestParams {
  const lights = Array.isArray(params.lights) ? params.lights : [];
  const main = lights.find((l) => l.isMain) || lights[0];

  // 1. 命中预设 → 直接取预设的 lighting_style（预设角度已与风格自洽）
  if (params.presetId && params.presetId !== 'custom') {
    const preset = findPreset(params.presetId);
    if (preset) return { lighting_style: preset.lightingStyle };
  }

  // 2. 自定义 → 按主灯角度映射方向族风格（按优先级，方案 §7 表）
  const h = main?.horizontalAngle ?? -45;
  const v = main?.verticalAngle ?? 60;
  return { lighting_style: mapAngleToStyle(h, v) };
}

/** 自定义模式：主灯水平/垂直角 → fal lighting_style（方向族阈值，按优先级） */
export function mapAngleToStyle(horizontalAngle: number, verticalAngle: number): LightingStyle {
  // 顶光（垂直角 ≥ 65°）
  if (verticalAngle >= 65) return 'spotlight';
  // 底光（垂直角 < 0°）：fal 枚举无独立底光项，映射为戏剧化
  if (verticalAngle < 0) return 'dramatic';
  // 侧后方（|水平角| > 135°）→ 轮廓光（纯逆光剪影 backlight 由「剪影」预设提供）
  if (Math.abs(horizontalAngle) > 135) return 'rim_light';
  // 侧向（45° < |水平角| ≤ 135°）
  if (Math.abs(horizontalAngle) > 45) return 'side_light';
  // 前方（|水平角| ≤ 45°）
  return 'front_light';
}

/** 主灯取值：lights 中 isMain 的首盏（数据防御：无主灯标记时取首盏） */
export function getMainLight(lights: LightSourceParams[] | undefined): LightSourceParams | undefined {
  if (!Array.isArray(lights) || lights.length === 0) return undefined;
  return lights.find((l) => l.isMain) || lights[0];
}
